import { InfrastructureStateLabel } from "@/interfaces/state_labels";
import { app } from "@/main";

// Explicit fallback icon/color for activities with no classification (e.g. a
// manually-reported "Other" event, which sends no initial_classification_id).
// Must render as visibly distinct from "Normal" and from an unmatched label,
// both of which otherwise fall through to a blank icon - see getIcon() below.
export const OTHER_UNCLASSIFIED_ICON = "mdi-help-circle-outline";
export const OTHER_UNCLASSIFIED_COLOR = "#8e24aa";

// Client-only sentinel id for the "Other" entry in classification picklists
// (ClassificationSelect.vue). There is no seeded "Other" InfrastructureStateLabel
// server-side; selecting this entry means no real classification applies and an
// other_description should be sent instead of a final/initial classification id.
export const OTHER_SENTINEL_ID = "__other__";

// Map pins are rendered as CSS background-images (see getIconSVG below), not
// icon-font glyphs, so the "Other" treatment needs its own image asset. None
// of the existing state_icons fit an "unclassified/other" concept, so this is
// a small self-contained SVG (purple circle + question mark) mirroring the
// mdi-help-circle-outline glyph used elsewhere for the same treatment.
export const OTHER_UNCLASSIFIED_ICON_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">` +
      `<circle cx="12" cy="12" r="11" fill="${OTHER_UNCLASSIFIED_COLOR}"/>` +
      `<path fill="#fff" d="M12 17.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm.1-11.5c2.09 0 3.4 1.33 3.4 3.06 0 1.24-.62 2.05-1.66 2.86-1 .78-1.34 1.16-1.34 2.08v.25h-1.9v-.32c0-1.31.53-1.98 1.62-2.8.86-.66 1.28-1.1 1.28-1.94 0-.9-.7-1.5-1.66-1.5-.86 0-1.5.42-1.87 1.24l-1.7-.9c.6-1.37 1.86-2.03 3.83-2.03z"/>` +
      `</svg>`,
  );

export const getLocalToken = (): string => {
  const token = localStorage.getItem("token");
  if (token) {
    return token;
  }
  return app.config.globalProperties.$cookies.get("access_token_cookie");
};

export const saveLocalToken = (token: string): void => {
  localStorage.setItem("token", token);
};

export const removeLocalToken = (): void => {
  localStorage.removeItem("token");
  app.config.globalProperties.$cookies.remove("access_token_cookie");
};

export const getMicrosoftCookieToken = (): string => {
  return app.config.globalProperties.$cookies.get("msad_access_token_cookie");
};
export const setMicrosoftCookieToken = (token: string): void => {
  app.config.globalProperties.$cookies.set("msad_access_token_cookie", token);
};

export const removeMicrosoftCookieToken = (): void => {
  app.config.globalProperties.$cookies.remove("msad_access_token_cookie");
};

export const removeMicrosoftRefreshCookieToken = (): void => {
  app.config.globalProperties.$cookies.remove("refresh_token_cookie");
};

/**
 * @description
 * Takes an Array<V>, and a grouping function,
 * and returns a Map of the array grouped by the grouping function.
 *
 * @param list An array of type V.
 * @param keyGetter A Function that takes the the Array type V as an input, and returns a value of type K.
 *                  K is generally intended to be a property key of V.
 *
 * @returns Map of the array grouped by the grouping function.
 */
export function groupBy<K, V>(
  list: Array<V>,
  keyGetter: (input: V) => K,
): Map<K, Array<V>> {
  const map = new Map<K, Array<V>>();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export const urlQueryString = (
  url: URL | string,
  params: Record<string, string>,
): string => {
  return url + "?" + new URLSearchParams(params).toString();
};

export enum displayStates {
  outputState = 1,
  infrastructureState = 2,
}

export const authHeaders = (
  token: string,
): { headers: { Authorization: string } } => {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const getIconSVG = (item: InfrastructureStateLabel): string => {
  if (item?.label?.toLowerCase().includes("leak")) {
    return require("@/assets/state_icons/leak.svg");
  }

  switch (item?.label) {
    case "Normal":
      return "check";
    case "No Data":
      return require("@/assets/state_icons/no-data.svg");
    case "Broken Fiber":
      return require("@/assets/state_icons/broken-fibre.svg");
    case "Broken Fibre":
      return require("@/assets/state_icons/broken-fibre.svg");
    case "Manual Digging":
      return require("@/assets/state_icons/manual-digging.svg");
    case "Mechanical Digging":
      return require("@/assets/state_icons/mechanical-digging.svg");
    case "Construction Activity":
      return require("@/assets/state_icons/mechanical-digging.svg");
    case "Vehicle":
      return require("@/assets/state_icons/vehicle.svg");
    case "Machinery":
      return require("@/assets/state_icons/machinery.svg");
    default:
      return require("@/assets/state_icons/default.svg");
  }
};

export const getIcon = (item: InfrastructureStateLabel): string => {
  switch (item?.label) {
    case "Normal":
      return "";
    case "No Data":
      return "mdi-access-point-off";
    case "Broken Fiber":
      return "mdi-heart-broken";
    case "Broken Fibre":
      return "mdi-heart-broken";
    case "Manual Digging":
      return "mdi-shovel";
    case "Mechanical Digging":
      return "mdi-excavator";
    case "Construction Activity":
      return "mdi-excavator";
    case "Suspected Small Leak":
      return "mdi-pipe-leak";
    case "Suspected Large Leak":
      return "mdi-pipe-leak";
    case "Leak (loud)":
      return "mdi-pipe-leak";
    case "Leak":
      return "mdi-pipe-leak";
    case "Vehicle":
      return "airport_shuttle";
    case "Machinery":
      return "mdi-cogs";
    default:
      return "";
  }
};

// unused
export const dbScale = (value: number): number => {
  return 20 * Math.log10(value);
};
